<style lang="scss">
  @use "../../../../assets/css/global.scss" as *;
  @use "../../../../assets/css/_colors.scss" as *;
  .tableWrapper {
    // overflow-x: auto;
    overflow: auto;
    --first-column-width: 50px;
    $grid-column-width: minmax(80px, 1fr);
    $columngap: 0.5em;
    $rowgap: 1em;

    // // TODO - edit according to @media for width??
    // height: 27rem;

    .tableContainer {
      display: grid;
      grid-template-rows: auto 1fr;

      .tableHeader {
        border-bottom: 1px solid $light-gray;
        display: grid;
        grid-template-columns: var(--first-column-width);
        grid-auto-columns: $grid-column-width;
        grid-auto-flow: column;
        column-gap: $columngap;
        row-gap: $rowgap;
        margin-bottom: 1rem;
        margin-top: 1rem;
        padding-bottom: 1rem;

        .headerCell {
          // font-size: clamp(0.7rem, 0.8vw, 0.8rem);
          background-color: transparent;
          border: 0;
          color: inherit;
          font-family: inherit;
          margin: 0;
          padding: 0;
          position: relative;
          text-align: center;
          text-transform: uppercase;
          @extend .text-title;

          p {
            display: inline;
            user-select: text;
          }

          &:not(.sortable) {
            cursor: text;
          }

          // TODO - tidy up these sortable classes??
          &.sortable {
            cursor: pointer;
            &:hover,
            &.selected {
              span {
                p {
                  user-select: none;
                  color: $blue;
                }
              }
            }
            &:not(.selected):hover {
              span {
                p {
                  user-select: none;
                  color: #4e73df;
                }
              }
            }

            span {
              position: relative;

              &.includeSortOrder {
                &::before,
                &::after {
                  font-size: 0.75rem;
                  right: -1rem;
                  position: absolute;
                }

                &::before {
                  content: "\f0dd";
                  font-family: "Font Awesome 6 Pro";
                }

                &::after {
                  content: "\f0de";
                  font-family: "Font Awesome 6 Pro";
                }
              }
            }

            &.sortedAsc {
              span {
                p {
                  color: $blue;
                }

                &::before {
                  color: $blue;
                }
              }
            }

            &.sortedDesc {
              span {
                p {
                  color: $blue;
                }

                &::after {
                  color: $blue;
                }
              }
            }
          }
        }
      }

      .tableBody {
        overflow-y: auto;

        .tableRow {
          display: grid;
          grid-template-columns: var(--first-column-width);
          grid-auto-columns: $grid-column-width;
          grid-auto-flow: column;
          column-gap: $columngap;
          row-gap: $rowgap;
          padding: 0.5rem 0;
          @extend .text-body-2;

          .tableCell {
            font-size: clamp(0.8rem, 0.8vw, 1rem);
            align-items: center;
            display: inline-flex;
            justify-content: center;
          }
        }
      }
    }
  }
</style>

<script>
  import { onMount, tick } from "svelte";
  import NameWrapper from "./NameWrapper.svelte";
  import {
    calculateRankingMapping,
    formatDatum,
    sortData,
  } from "./utils/utils";

  export let data = [];
  export let component = "";
  export let columns = [];
  export let sortBy = "";
  export let sortOrder = -1;
  export let includeSortOrder = false;
  export let height = "27rem";
  export let headerCellFontSize = "clamp(0.7rem, 0.8vw, 0.8rem)";

  const tableRows = [];
  const firstColumnPadding = 35;

  let tableContainer;
  let tableBody;
  let firstColumnWidth = 400;
  let firstColumnElements = [];

  const handleSort = (column) => {
    const col = columns.find((c) => c.key === column);
    if (col && col.sortable !== false) {
      if (sortBy === column && includeSortOrder) {
        sortOrder = -sortOrder;
      } else {
        sortBy = column;
        sortOrder = -1;
      }
    }
  };

  const updateFirstColumnWidth = () => {
    if (firstColumnElements.length > 0) {
      const maxWidth = Math.max(
        ...firstColumnElements.map((el) => el.firstChild.clientWidth)
      );
      firstColumnWidth = maxWidth + firstColumnPadding;
      tableContainer.style.setProperty(
        "--first-column-width",
        `${firstColumnWidth}px`
      );
    }
  };

  $: sortedData = sortBy ? sortData(data, sortBy, sortOrder) : data;
  $: currentUserIndex = sortedData.findIndex((item) => item.isCurrentUser);

  let rankedData = [];
  let rankingMapping = {};

  $: {
    if (sortBy) {
      rankingMapping = calculateRankingMapping(data, sortBy);

      rankedData = sortedData.map((item) => ({
        ...item,
        ranking: rankingMapping[item[sortBy]],
      }));
    } else {
      rankedData = sortedData;
    }
  }

  $: {
    firstColumnElements = firstColumnElements.filter(Boolean);
    if (firstColumnElements.length > 0) {
      updateFirstColumnWidth();
    }
  }

  onMount(async () => {
    if (currentUserIndex !== -1 && tableRows[currentUserIndex]) {
      const rowElement = tableRows[currentUserIndex];
      const containerElement = tableBody;
      const rowOffsetTop = rowElement.offsetTop;
      const containerOffsetTop = containerElement.offsetTop;
      const scrollPosition = rowOffsetTop - containerOffsetTop;

      containerElement.scrollTop = scrollPosition - 60;
    }
    await tick();
    updateFirstColumnWidth();
  });
</script>

<div class="tableWrapper" style="height: {height}">
  <div class="tableContainer" bind:this={tableContainer}>
    <div class="tableHeader" style="--first-column-width: {firstColumnWidth}px">
      {#each columns as column}
        <button
          class="headerCell"
          class:includeSortOrder
          class:sortedAsc={includeSortOrder &&
            sortBy === column.key &&
            sortOrder === 1}
          class:sortedDesc={includeSortOrder &&
            sortBy === column.key &&
            sortOrder === -1}
          class:sortable={column.sortable !== false}
          class:selected={sortBy === column.key}
          on:click={() => handleSort(column.key)}
          style="font-size:{headerCellFontSize}"
        >
          <span><p>{column.label}</p></span>
        </button>
      {/each}
    </div>
    <div class="tableBody" bind:this={tableBody}>
      {#each rankedData as item, i}
        <div class="tableRow" bind:this={tableRows[i]}>
          {#each columns as column}
            {#if column.initialColumn}
              <div
                class="tableCell"
                bind:this={firstColumnElements[i]}
                style="justify-content: left; --first-column-width: {firstColumnWidth}px"
              >
                {#if sortBy}
                  <NameWrapper user={item} ranking={item.ranking} showRanking />
                {:else}
                  <NameWrapper user={item} displayAsIcon="true" />
                {/if}
              </div>
            {:else}
              <div class="tableCell">
                {formatDatum(
                  item[column.key],
                  component === "benchmarks" ? "%" : column.type
                )}
              </div>
            {/if}
          {/each}
        </div>
      {/each}
    </div>
  </div>
</div>
